import { AxiosError } from 'axios';
import api from '../api/apiClient';
import { PortfolioImage, PortfolioData, PortfolioImageUpload } from '../types/portfolio';

/**
 * Uploads a new image to the stylist's portfolio.
 * POST /portfolio/upload
 */
export async function addPortfolioImage(
  formData: FormData
): Promise<PortfolioImage[]> {
  try {
    const response = await api.portfolio.uploadPortfolioImage(formData);
    return response.data.data;
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error('Add portfolio image error:', err.response?.data || err.message);
    throw err;
  }
}

/**
 * Uploads a new video to the stylist's portfolio.
 * POST /portfolio/upload-video
 */
export async function addPortfolioVideo(
  formData: FormData
): Promise<PortfolioImage[]> {
  try {
    const response = await api.portfolio.uploadPortfolioVideo(formData);
    return response.data.data;
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error('Add portfolio video error:', err.response?.data || err.message);
    throw err;
  }
}

/**
 * Deletes a portfolio item by ID.
 * DELETE /portfolio/:itemId
 */
export async function deletePortfolioImage(
  itemId: string
): Promise<PortfolioImage[]> {
  try {
    const response = await api.portfolio.deletePortfolioItem(itemId);
    return response.data.data;
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error('Delete portfolio image error:', err.response?.data || err.message);
    throw err;
  }
}

/**
 * Updates metadata for a portfolio item.
 * PUT /portfolio/:itemId
 */
export async function updatePortfolioImage(
  itemId: string,
  data: PortfolioImageUpload
): Promise<PortfolioImage[]> {
  try {
    const response = await api.portfolio.updatePortfolioImage(itemId, data);
    return response.data.data;
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error('Update portfolio image error:', err.response?.data || err.message);
    throw err;
  }
}

/**
 * Retrieves the full portfolio for a stylist.
 * GET /portfolio/:stylistId
 */
export async function getPortfolio(
  stylistId: string
): Promise<PortfolioData> {
  try {
    const response = await api.portfolio.getPortfolio(stylistId);
    return response.data.data;
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error('Get portfolio error:', err.response?.data || err.message);
    throw err;
  }
}

/**
 * Retrieves the current user's portfolio items.
 * GET /portfolio/me
 */
export async function getMyPortfolio(): Promise<PortfolioImage[]> {
  try {
    const response = await api.getWithParams<{ data: PortfolioImage[] }>('/portfolio/me');
    return response.data.data;
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error('Get my portfolio error:', err.response?.data || err.message);
    throw err;
  }
}

/**
 * Reorders portfolio items based on provided IDs.
 * PUT /portfolio/reorder
 */
export async function reorderPortfolio(
  itemIds: string[]
): Promise<void> {
  try {
    await api.putWithData<void>('/portfolio/reorder', { itemIds });
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error('Reorder portfolio error:', err.response?.data || err.message);
    throw err;
  }
}

/**
 * Retrieves portfolio images for a specific service.
 * GET /portfolio/service/:serviceId
 */
export async function getPortfolioByService(
  serviceId: string
): Promise<{ service: { name: string; category: string }; images: PortfolioImage[] }> {
  try {
    const response = await api.getWithParams<{
      data: { service: { name: string; category: string }; images: PortfolioImage[] }
    }>(`/portfolio/service/${serviceId}`);
    return response.data.data;
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error('Get portfolio by service error:', err.response?.data || err.message);
    throw err;
  }
}

/**
 * Groups a flat array of portfolio images by category.
 */
export function groupImagesByCategory(
  images: PortfolioImage[]
): Record<string, PortfolioImage[]> {
  return images.reduce((acc, image) => {
    const key = image.category || 'uncategorized';
    if (!acc[key]) acc[key] = [];
    acc[key].push(image);
    return acc;
  }, {} as Record<string, PortfolioImage[]>);
}

/**
 * Filters images to only those marked featured.
 */
export function getFeaturedImages(
  images: PortfolioImage[]
): PortfolioImage[] {
  return images.filter(img => img.featured);
}

/**
 * Filters images by a given service ID.
 */
export function filterByService(
  images: PortfolioImage[],
  serviceId: string
): PortfolioImage[] {
  return images.filter(img => img.serviceId === serviceId);
}

/**
 * Searches portfolio images by tags inclusion.
 */
export function searchByTags(
  images: PortfolioImage[],
  tags: string[]
): PortfolioImage[] {
  return images.filter(img =>
    img.tags?.some(tag => tags.includes(tag))
  );
}

/**
 * Validates an image file for type and size limits (<=5MB).
 */
export function validateImageFile(
  file: File
): boolean {
  const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
  return validTypes.includes(file.type) && file.size <= 5 * 1024 * 1024;
}

/**
 * Prepares FormData for uploading an image with metadata fields.
 */
export function prepareImageUpload(
  file: File,
  data: PortfolioImageUpload
): FormData {
  const form = new FormData();
  form.append('image', file);
  if (data.caption) form.append('caption', data.caption);
  if (data.category) form.append('category', data.category);
  if (data.serviceId) form.append('serviceId', data.serviceId);
  if (data.tags) form.append('tags', JSON.stringify(data.tags));
  if (data.variation) form.append('variation', JSON.stringify(data.variation));
  return form;
}

/**
 * Combined service object for easy imports.
 */
export const portfolioService = {
  addPortfolioImage,
  addPortfolioVideo,
  deletePortfolioImage,
  updatePortfolioImage,
  getPortfolio,
  getMyPortfolio,
  reorderPortfolio,
  getPortfolioByService,
  groupImagesByCategory,
  getFeaturedImages,
  filterByService,
  searchByTags,
  validateImageFile,
  prepareImageUpload,
};

export default portfolioService;
